(ns io.daskul.webapp.widgets.form.notes
  "Notes widget for keyed-map collection fields (F-044).

   Displays notes as a chronological list with add/delete.
   Uses the auto-generated collection endpoints:
   - POST /<resource>/:id/<field> to append
   - DELETE /<resource>/:id/<field>/:entry-id to remove"
  (:require
   [uix.core :as uix :refer [defui $]]
   [re-frame.core :as rf]
   [ajax.core :as ajax]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]
   [io.daskul.webapp.aggregates.events :as agg.events]))

(defn- format-relative [iso-string]
  (when iso-string
    (try
      (let [date (js/Date. iso-string)
            now (js/Date.)
            diff-ms (- (.getTime now) (.getTime date))
            diff-mins (/ diff-ms 1000 60)
            diff-hours (/ diff-mins 60)
            diff-days (/ diff-hours 24)]
        (cond
          (< diff-mins 1) "just now"
          (< diff-mins 60) (str (js/Math.floor diff-mins) "m ago")
          (< diff-hours 24) (str (js/Math.floor diff-hours) "h ago")
          (< diff-days 30) (str (js/Math.floor diff-days) "d ago")
          :else (.toLocaleDateString date "en-US" #js {:month "short" :day "numeric"})))
      (catch :default _ iso-string))))

(defn- collection-url [agg-key id field-name]
  (let [resource (agg.events/get-resource-name agg-key)]
    (str (agg.events/get-api-base) "/" resource "/" id "/" (name field-name))))

(defn- append-note! [agg-key id field-name text on-done]
  (let [url (collection-url agg-key id field-name)]
    (ajax/POST url
      {:params {:text text}
       :headers (agg.events/get-auth-header)
       :format (ajax/json-request-format)
       :response-format (ajax/json-response-format {:keywords? true})
       :handler (fn [_]
                  (on-done)
                  (rf/dispatch [::agg.events/get agg-key id]))
       :error-handler (fn [err]
                        (js/console.error "Failed to append note" err))})))

(defn- delete-note! [agg-key id field-name entry-id]
  (let [url (str (collection-url agg-key id field-name) "/" entry-id)]
    (ajax/DELETE url
      {:headers (agg.events/get-auth-header)
       :response-format (ajax/json-response-format {:keywords? true})
       :handler (fn [_]
                  (rf/dispatch [::agg.events/get agg-key id]))
       :error-handler (fn [err]
                        (js/console.error "Failed to delete note" err))})))

(defui note-item
  [{:keys [note agg-key record-id field-name state-display]}]
  (let [{:keys [id text date state]} note
        [hovered? set-hovered!] (uix/use-state false)
        [confirm? set-confirm!] (uix/use-state false)
        state-config (when state
                       (or (get-in state-display [:values (keyword state)])
                           (get-in state-display [:values state])))
        badge-label (or (:label state-config) state)]
    ($ ui/box {:onMouseEnter #(set-hovered! true)
               :onMouseLeave #(do (set-hovered! false) (set-confirm! false))
               :style {:borderRadius 4
                       :marginLeft -6 :marginRight -6
                       :padding "4px 6px"
                       :backgroundColor (when hovered? "var(--mantine-color-gray-0)")
                       :transition "background-color 150ms ease"}}
      ($ ui/multiline-text {:size "sm"} text)
      ($ ui/group {:justify "space-between" :mt 2}
        ($ ui/group {:gap 6}
          (when state
            ($ ui/text {:size "xs" :c "dimmed"} badge-label))
          (when date
            ($ ui/text {:size "xs" :c "dimmed"} (format-relative date))))
        ($ ui/popover {:opened confirm? :position "bottom-end" :withArrow true
                       :onClose #(set-confirm! false)}
          ($ ui/popover-target
            ($ ui/action-icon
              {:size "xs" :variant "subtle" :color "red"
               :style {:opacity (if hovered? 1 0)
                       :transition "opacity 150ms ease"}
               :onClick #(set-confirm! true)}
              ($ icons/icon-trash {:size 12})))
          ($ ui/popover-dropdown
            ($ ui/stack {:gap "xs"}
              ($ ui/text {:size "xs"} "Delete this note?")
              ($ ui/group {:gap "xs"}
                ($ ui/button {:size "compact-xs" :variant "light" :color "gray"
                              :onClick #(set-confirm! false)} "Cancel")
                ($ ui/button {:size "compact-xs" :color "red"
                              :onClick (fn []
                                         (set-confirm! false)
                                         (delete-note! agg-key record-id field-name id))}
                  "Delete")))))))))

(defn- keyed-map->sorted-vec
  "Transform keyed-map {uuid {:text ...}} to sorted vector [{:id uuid :text ...}].
   Sorts by :date descending (newest first).
   Handles both keyword and string keys from JSON deserialization."
  [notes-map]
  (when (and notes-map (map? notes-map) (seq notes-map))
    (->> notes-map
         (keep (fn [[k v]]
                 (when (map? v)
                   (assoc v :id (name k)))))
         (sort-by :date #(compare %2 %1))
         vec)))

(defui notes-widget
  "Sidebar notes widget for keyed-map collection fields.

   Props:
   - agg-key: keyword — aggregate key
   - record-id: uuid — record being edited
   - field-name: keyword — collection field name (e.g., :notes)
   - notes: keyed-map from API {uuid {:text ...}} — transformed to sorted vector
   - state-display: FSM state→UI config from /_config for badge coloring"
  [{:keys [agg-key record-id field-name notes state-display]}]
  (let [notes (keyed-map->sorted-vec notes)
        [text set-text!] (uix/use-state "")
        [adding? set-adding!] (uix/use-state false)
        handle-add (fn []
                     (when (seq (.trim text))
                       (set-adding! true)
                       (append-note! agg-key record-id field-name (.trim text)
                                     (fn []
                                       (set-text! "")
                                       (set-adding! false)))))]
    ($ ui/stack {:gap "sm"}
      ($ ui/text {:fw 600 :size "sm"} "Notes")

      ;; Add note form
      ($ ui/textarea
        {:placeholder "Add a note..."
         :value text
         :onChange #(set-text! (.. % -target -value))
         :minRows 3
         :autosize true
         :size "sm"})
      ($ ui/button
        {:size "compact-xs" :variant "light" :fullWidth true
         :onClick handle-add
         :loading adding?
         :disabled (empty? (.trim text))}
        "Add Note")

      ;; Notes list
      (if (seq notes)
        ($ ui/stack {:gap 0}
          (for [note notes]
            ($ note-item
              {:key (:id note)
               :note note
               :agg-key agg-key
               :record-id record-id
               :field-name field-name
               :state-display state-display})))
        ($ ui/text {:size "xs" :c "dimmed"} "No notes yet")))))
